import React, { useState } from 'react';
import { ExternalLink, Copy, CheckCircle, Check } from 'lucide-react';
import { ReportingAgency } from '../types';
import { Button } from './Button';
import { Card } from './Card';
interface ReportingAgencyCardProps {
  agency: ReportingAgency;
}
const COUNTRY_FLAGS: Record<string, string> = {
  Indonesia: '🇮🇩',
  Malaysia: '🇲🇾',
  Singapore: '🇸🇬',
  Thailand: '🇹🇭',
  Vietnam: '🇻🇳',
  Philippines: '🇵🇭',
  Cambodia: '🇰🇭',
  Laos: '🇱🇦',
  Myanmar: '🇲🇲',
  Brunei: '🇧🇳',
  International: '🌏',
  USA: '🇺🇸'
};
export function ReportingAgencyCard({
  agency
}: ReportingAgencyCardProps) {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const flag = COUNTRY_FLAGS[agency.country] || '🏳️';
  return <Card className="h-full flex flex-col hover:shadow-md transition-shadow duration-300 border-l-4 border-l-transparent hover:border-l-primary-500" data-id="element-232">
      <div className="flex items-start justify-between mb-4" data-id="element-233">
        <div className="flex-1" data-id="element-234">
          <div className="flex items-center gap-2 mb-1" data-id="element-235">
            <span className="text-xl" role="img" aria-label={agency.country} data-id="element-236">
              {flag}
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-237">
              {agency.country}
            </span>
          </div>

          <div className="flex items-center gap-2" data-id="element-238">
            <h3 className="font-bold text-lg text-slate-900 leading-tight" data-id="element-239">
              {agency.name}
            </h3>
            {agency.verified && <div className="relative group/tooltip" data-id="element-240">
                <CheckCircle className="h-5 w-5 text-blue-500 fill-blue-50" aria-label="Verified Official Agency" data-id="element-241" />
                <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-slate-800 rounded opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" data-id="element-242">
                  Verified Official
                </span>
              </div>}
          </div>

          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 mt-2 border border-slate-200" data-id="element-243">
            {agency.type}
          </span>
        </div>
      </div>

      <p className="text-slate-600 text-sm mb-6 flex-grow leading-relaxed" data-id="element-244">
        {agency.description}
      </p>

      <div className="grid grid-cols-2 gap-3 mt-auto pt-4 border-t border-slate-100" data-id="element-245">
        <Button variant="outline" size="sm" onClick={() => window.open(agency.url, '_blank')} className="justify-center w-full hover:bg-primary-50 hover:text-primary-700 hover:border-primary-200" data-id="element-246">
          <ExternalLink className="h-3.5 w-3.5 mr-2" data-id="element-247" />
          Visit
        </Button>
        <Button variant={copied ? 'success' : 'ghost'} size="sm" onClick={() => copyToClipboard(agency.url)} className={`justify-center w-full transition-all duration-200 ${copied ? 'bg-green-50 text-green-700 border border-green-200' : 'text-slate-500 hover:text-slate-900 bg-slate-50 hover:bg-slate-100'}`} data-id="element-248">
          {copied ? <>
              <Check className="h-3.5 w-3.5 mr-2" data-id="element-249" />
              Copied
            </> : <>
              <Copy className="h-3.5 w-3.5 mr-2" data-id="element-250" />
              Copy Link
            </>}
        </Button>
      </div>
    </Card>;
}